import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import FanVerseLogo from "@/components/FanVerseLogo";
import { Mail, ArrowLeft, CheckCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

const ForgotPasswordPage = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
      toast.success("Reset link sent! Check your email.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Something went wrong. Please try again.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#030712] px-4">
      <div className="absolute inset-0 overflow-hidden">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/f60715c2-3b6b-4c99-a6c8-5c96b98e6cf9/images/6a0a81e49ef10480dd8f68fed5fd73786eac1a2e88374dc1978201d1210e22e0.png"
          alt="Background"
          className="w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#030712] via-[#030712]/80 to-[#030712]/60" />
      </div>

      <div className="relative z-10 w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 text-yellow-500 mb-8">
            <FanVerseLogo size={44} showText={false} />
        </Link>

        <div className="card-surface p-8">
          {sent ? (
            <div className="text-center" data-testid="reset-sent-message">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-white mb-2 font-['Outfit']">Check Your Email</h1>
              <p className="text-slate-400 text-sm mb-6">
                If an account exists for <strong className="text-white">{email}</strong>, we've sent a password reset link. Check your inbox and spam folder.
              </p>
              <div className="space-y-3">
                <Button onClick={() => setSent(false)} variant="ghost" className="w-full text-yellow-500 hover:text-yellow-400" data-testid="try-another-email">
                  Try another email
                </Button>
                <Link to="/login" className="block text-center text-sm text-slate-400 hover:text-white transition-colors" data-testid="back-to-login-link">
                  <ArrowLeft className="w-4 h-4 inline mr-1" />
                  Back to login
                </Link>
              </div>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-bold text-white text-center mb-2 font-['Outfit']">{t("auth.resetPassword")}</h1>
              <p className="text-slate-400 text-center text-sm mb-6">
                Enter your email and we'll send you a link to reset your password.
              </p>

              {error && (
                <div className="bg-red-500/10 border border-red-500/30 text-red-500 px-4 py-3 text-sm mb-6" data-testid="forgot-error">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4" data-testid="forgot-password-form">
                <div>
                  <Label htmlFor="email" className="text-slate-300 text-sm">Email Address</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="pl-10 input-dark"
                      required
                      data-testid="forgot-email"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-primary py-3"
                  data-testid="forgot-submit"
                >
                  {loading ? "..." : t("auth.sendResetLink")}
                </Button>
              </form>

              <p className="text-center text-slate-400 text-sm mt-6">
                <Link to="/login" className="text-yellow-500 hover:underline" data-testid="back-to-login">
                  <ArrowLeft className="w-4 h-4 inline mr-1" />
                  Back to login
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
